import { DATA_CHANNEL_LABEL } from '../../shared/constants.js';
import type { ClientMessage } from '../../shared/signaling.js';
import type { FileMeta } from '../../shared/transfer.js';
import { t } from './i18n/index.js';
import { fetchIceServers } from './ice.js';
import { waitForDataChannel, watchConnectionFailure, wireSignaling } from './peer.js';
import { receiveFile } from './receive-file.js';
import type { ReceivedFile } from './receive-file.js';
import { formatRoute, watchRoute } from './route-stats.js';
import { SignalingClient, signalingUrl } from './signaling-client.js';
import type { FileSink } from './sinks/file-sink.js';
import { createProgressPanel } from './ui/progress.js';
import type { ProgressPanel } from './ui/progress.js';

export type ReceiveSessionHooks = Readonly<{
  onConnected: () => void;
  onChannelOpen: () => void;
  /** Called with the announced file; resolves with the sink to write into. The sender waits meanwhile. */
  chooseSink: (meta: FileMeta) => Promise<FileSink>;
  /** The sink exists; the page should mount the progress panel now. */
  onTransferStart: (progress: ProgressPanel) => void;
}>;

/**
 * Receiver half of a transfer on a fresh signaling connection: joins with the given message,
 * negotiates the peer connection and writes the file into the chosen sink.
 */
export const runReceiveSession = async (join: ClientMessage, hooks: ReceiveSessionHooks): Promise<ReceivedFile> => {
  const iceServers = await fetchIceServers();
  const pc = new RTCPeerConnection({ iceServers });
  const failure = watchConnectionFailure(pc);
  const channelPromise = waitForDataChannel(pc, DATA_CHANNEL_LABEL);

  const signaling = await SignalingClient.connect(signalingUrl(window.location));
  const unsubscribe = wireSignaling(pc, signaling);
  const joined = signaling.waitFor((message): message is { type: 'joined'; roomId: string } => message.type === 'joined');
  signaling.send(join);
  await joined;
  hooks.onConnected();

  const channel = await Promise.race([channelPromise, failure]);
  hooks.onChannelOpen();

  const progress = createProgressPanel(t('progress.receiving'));
  let stopWatchingRoute: () => void = () => undefined;
  let startedAtMs = Date.now();
  const received = await Promise.race([
    receiveFile(channel, {
      chooseSink: async (meta) => {
        const sink = await hooks.chooseSink(meta);
        hooks.onTransferStart(progress);
        stopWatchingRoute = watchRoute(pc, (route) => progress.setRoute(formatRoute(route)));
        startedAtMs = Date.now();
        return sink;
      },
      onProgress: (receivedBytes, totalBytes) =>
        progress.update({ doneBytes: receivedBytes, totalBytes, startedAtMs, nowMs: Date.now() }),
    }),
    failure,
  ]);

  stopWatchingRoute();
  progress.complete(t('progress.arrivedDetail'));
  unsubscribe();
  channel.close();
  pc.close();
  signaling.close();
  return received;
};
